import {adjustProductAccessUsage} from '@account/api';
import {trackEvent} from '@account/analytics';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

// The backend requires a UUID `Idempotency-Key` generated once per submission.
// It claims the key before calling the Usage API, so retrying a submission with
// the same key replays the original result instead of applying the adjustment
// twice. Reusing a key with a *different* body is rejected as a 422, so the key
// is tied to the payload: retrying the same submission reuses it, while editing
// the form and resubmitting earns a fresh one.
const fingerprint = body => {
  const safe = body ?? {};
  return JSON.stringify(safe, Object.keys(safe).sort());
};

/**
 * Usage Reversal: apply a signed usage adjustment to the Usage API bucket
 * backing a ProductAccess. On success, refresh the product-access lists/usage so
 * the workspace table's "Remaining" reflects the change.
 */
export const useAdjustProductAccessUsage = productAccessId => {
  const queryClient = useQueryClient();
  const idempotency = useRef({fingerprint: null, key: null});

  return useMutation(
    body => {
      const current = fingerprint(body);
      if (idempotency.current.fingerprint !== current) {
        idempotency.current = {fingerprint: current, key: uuidv4()};
      }
      return adjustProductAccessUsage(
        productAccessId,
        body,
        idempotency.current.key
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productAccesses']);
        queryClient.invalidateQueries(['productAccessUsage', productAccessId]);
        queryClient.invalidateQueries([
          'productAccessLedgerEntries',
          productAccessId,
        ]);
        // Privileged financial write — emit an audit/on-call signal both ways.
        // Kept last so a telemetry hiccup can't skip the cache refresh above.
        trackEvent('Usage adjustment applied', {productAccessId});
      },
      onError: err => {
        trackEvent('Usage adjustment error', {
          productAccessId,
          message: err?.message,
        });
      },
    }
  );
};

export default useAdjustProductAccessUsage;
